import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import apiClient from '../../api/client';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../components/ui/Toast';
import { formatEUR, formatDate } from '../../lib/format';
import {
  Tabs,
  Modal,
  Empty,
  thCls,
  thNum,
  tdCls,
  tdNum,
  apiMessage,
  cmd,
  type Step,
} from '../../components/collection/collectionShared';

/**
 * Relances — the letters go in order, never skipping a step, with a minimum gap between two
 * letters. A dispute, a payment promise or a pause stops them for the customer concerned. A letter
 * that could not be sent (no address, no mail relay) is listed as such and counts for nothing.
 */

interface QueueItem {
  customer: {
    id: string;
    name: string;
    email: string | null;
    pause: { reason: string; until: string | null } | null;
    promise: { date: string } | null;
  };
  step: Step;
  amount: number;
  held: string | null;
  items: Array<{ invoiceId: string; number: string; late: number; remaining: number }>;
  lastLetter: { date: string; step: string } | null;
}
interface Letter {
  id: string;
  number: string;
  customerId: string;
  customerName: string;
  step: string;
  label: string;
  date: string;
  invoices: string[];
  amount: number;
  penalties: number;
  auto: boolean;
  by: string | null;
  status: 'PENDING' | 'SENT' | 'FAILED';
  error: string | null;
  to: string | null;
}
interface Data {
  today: string;
  autoDunning: boolean;
  hour: string;
  minGapDays: number;
  queue: QueueItem[];
  history: Letter[];
}
type Tab = 'file' | 'suspendues' | 'historique';

export default function CollectionDunning() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [data, setData] = useState<Data | null>(null);
  const [tab, setTab] = useState<Tab>('file');
  const [mail, setMail] = useState<{
    title: string;
    to: string | null;
    subject: string;
    body: string;
    send?: () => void;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiClient
      .get<Data>('/collection/dunning')
      .then((r) => setData(r.data))
      .catch((e) => showApiError(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);

  const send = async (customerId?: string) => {
    setBusy(true);
    try {
      const r = await apiClient.post<{
        sent: Array<{ number: string; status: string; error: string | null }>;
      }>('/collection/dunning/send', { customerId }, cmd());
      const ok = r.data.sent.filter((s) => s.status === 'SENT').length;
      const ko = r.data.sent.filter((s) => s.status === 'FAILED');
      push({
        tone: ko.length ? 'warning' : 'success',
        message:
          t('collection.dunning.sent', { count: ok }) +
          (ko.length
            ? ` ${t('collection.dunning.failedSome', { count: ko.length, error: ko[0].error })}`
            : ''),
      });
      setMail(null);
      load();
    } catch (e) {
      push({ tone: 'error', message: apiMessage(t, e) });
    } finally {
      setBusy(false);
    }
  };
  const preview = async (q: QueueItem) => {
    try {
      const r = await apiClient.get<{ to: string | null; subject: string; body: string }>(
        `/collection/dunning/preview?customerId=${q.customer.id}&step=${q.step.key}`
      );
      setMail({
        title: t('collection.dunning.previewTitle', { name: q.customer.name }),
        ...r.data,
        send: () => send(q.customer.id),
      });
    } catch (e) {
      push({ tone: 'error', message: apiMessage(t, e) });
    }
  };
  const showLetter = async (l: Letter) => {
    try {
      const r = await apiClient.get<{ subject: string; body: string; to_email: string | null }>(
        `/collection/dunning/letters/${l.id}`
      );
      setMail({
        title: `${l.number} · ${l.label}`,
        to: r.data.to_email,
        subject: r.data.subject,
        body: r.data.body,
      });
    } catch (e) {
      push({ tone: 'error', message: apiMessage(t, e) });
    }
  };
  const resume = async (customerId: string) => {
    try {
      await apiClient.delete(`/collection/customers/${customerId}/pause`, cmd());
      load();
    } catch (e) {
      push({ tone: 'error', message: apiMessage(t, e) });
    }
  };

  const toSend = (data?.queue ?? []).filter((q) => !q.held);
  const held = (data?.queue ?? []).filter((q) => q.held);
  const stepTone = (s: Step) => (s.block ? 'destructive' : s.penalties ? 'warning' : 'muted');

  return (
    <div>
      <PageHeader
        title={t('collection.dunning.title')}
        description={t('collection.dunning.description', { days: data?.minGapDays ?? 7 })}
      />
      <div className="rounded-lg border bg-card shadow-sm">
        <Tabs<Tab>
          value={tab}
          onChange={setTab}
          tabs={[
            {
              key: 'file',
              label: t('collection.dunning.tab.file'),
              count: toSend.length,
              alert: true,
            },
            {
              key: 'suspendues',
              label: t('collection.dunning.tab.suspendues'),
              count: held.length,
            },
            {
              key: 'historique',
              label: t('collection.dunning.tab.historique'),
              count: data?.history.length ?? 0,
            },
          ]}
        />
        <div className="overflow-x-auto">
          {data === null ? null : tab === 'file' ? (
            <>
              {toSend.length ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className={thCls}>{t('collection.col.customer')}</th>
                      <th className={thCls}>{t('collection.col.step')}</th>
                      <th className={thCls}>{t('collection.col.invoices')}</th>
                      <th className={thNum}>{t('collection.col.amount')}</th>
                      <th className={thCls}>{t('collection.col.lastLetter')}</th>
                      <th className={thCls} />
                    </tr>
                  </thead>
                  <tbody>
                    {toSend.map((q) => (
                      <tr
                        key={`${q.customer.id}-${q.step.key}`}
                        className="border-b last:border-b-0"
                      >
                        <td className={tdCls}>
                          <Link
                            to={`/collection/accounts/${q.customer.id}`}
                            className="font-semibold hover:underline"
                          >
                            {q.customer.name}
                          </Link>
                          <div className="text-xs text-muted-foreground">
                            {q.customer.email ?? t('collection.dunning.noEmail')}
                          </div>
                        </td>
                        <td className={tdCls}>
                          <Badge variant={stepTone(q.step)}>{q.step.label}</Badge>
                          {q.step.block && (
                            <div className="text-xs text-muted-foreground">
                              {t('collection.dunning.blocksOnSend')}
                            </div>
                          )}
                        </td>
                        <td className={tdCls}>
                          {q.items.map((i) => (
                            <div key={i.invoiceId} className="whitespace-nowrap font-mono text-xs">
                              {i.number} · {t('collection.common.days', { n: i.late })}
                            </div>
                          ))}
                        </td>
                        <td className={tdNum}>{formatEUR(q.amount)}</td>
                        <td className={tdCls}>
                          {q.lastLetter
                            ? `${formatDate(q.lastLetter.date)} · ${q.lastLetter.step}`
                            : t('collection.dunning.never')}
                        </td>
                        <td className={tdCls}>
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="outline" onClick={() => preview(q)}>
                              {t('collection.dunning.preview')}
                            </Button>
                            <Button size="sm" onClick={() => send(q.customer.id)} disabled={busy}>
                              {t('collection.dunning.send')}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <Empty>{t('collection.dunning.nothing')}</Empty>
              )}
              <div className="flex flex-wrap items-center gap-3 border-t px-4 py-3">
                <span className="flex-1 text-sm text-muted-foreground">
                  {data.autoDunning
                    ? t('collection.dunning.autoOn', { hour: data.hour })
                    : t('collection.dunning.autoOff')}
                </span>
                {toSend.length > 0 && (
                  <Button onClick={() => send()} disabled={busy}>
                    {t('collection.dunning.sendAll', { count: toSend.length })}
                  </Button>
                )}
              </div>
            </>
          ) : tab === 'suspendues' ? (
            held.length ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className={thCls}>{t('collection.col.customer')}</th>
                    <th className={thCls}>{t('collection.col.waitingStep')}</th>
                    <th className={thNum}>{t('collection.col.amount')}</th>
                    <th className={thCls}>{t('collection.col.reason')}</th>
                    <th className={thCls} />
                  </tr>
                </thead>
                <tbody>
                  {held.map((q) => (
                    <tr key={`${q.customer.id}-${q.step.key}`} className="border-b last:border-b-0">
                      <td className={tdCls}>
                        <b>{q.customer.name}</b>
                      </td>
                      <td className={tdCls}>{q.step.label}</td>
                      <td className={tdNum}>{formatEUR(q.amount)}</td>
                      <td className={tdCls}>{q.held}</td>
                      <td className={tdCls}>
                        {q.customer.pause && (
                          <Button size="sm" variant="outline" onClick={() => resume(q.customer.id)}>
                            {t('collection.account.resume')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <Empty>{t('collection.dunning.noneHeld')}</Empty>
            )
          ) : data.history.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                  {[
                    'letter',
                    'date',
                    'customer',
                    'step',
                    'invoices',
                    'amount',
                    'penalties',
                    'sentBy',
                    '',
                  ].map((c, n) => (
                    <th key={n} className={c === 'amount' || c === 'penalties' ? thNum : thCls}>
                      {c ? t(`collection.col.${c}`) : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.history.map((l) => (
                  <tr key={l.id} className="border-b last:border-b-0">
                    <td className={tdCls}>
                      <span className="font-mono text-xs">{l.number}</span>
                      {l.status === 'FAILED' && (
                        <div
                          className="text-xs font-semibold text-destructive"
                          title={l.error ?? ''}
                        >
                          {t('collection.dunning.notSent')}
                        </div>
                      )}
                    </td>
                    <td className={tdCls}>{formatDate(l.date)}</td>
                    <td className={tdCls}>{l.customerName}</td>
                    <td className={tdCls}>{l.label}</td>
                    <td className={tdCls}>
                      <span className="font-mono text-xs">{l.invoices.join(', ')}</span>
                    </td>
                    <td className={tdNum}>{formatEUR(l.amount)}</td>
                    <td className={tdNum}>{l.penalties ? formatEUR(l.penalties) : '—'}</td>
                    <td className={tdCls}>
                      {l.auto ? t('collection.common.system') : l.by}
                      {l.auto && (
                        <div className="text-xs text-muted-foreground">
                          {t('collection.dunning.automatic')}
                        </div>
                      )}
                    </td>
                    <td className={tdCls}>
                      <Button size="sm" variant="ghost" onClick={() => showLetter(l)}>
                        {t('collection.dunning.view')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty>{t('collection.dunning.noneSent')}</Empty>
          )}
        </div>
      </div>
      {mail && (
        <Modal
          title={mail.title}
          lede={
            mail.to ? t('collection.dunning.to', { to: mail.to }) : t('collection.dunning.noEmail')
          }
          onClose={() => setMail(null)}
          wide
          footer={
            <>
              <Button variant="outline" onClick={() => setMail(null)}>
                {t('collection.common.close')}
              </Button>
              {mail.send && (
                <Button onClick={mail.send} disabled={busy}>
                  {t('collection.dunning.send')}
                </Button>
              )}
            </>
          }
        >
          <div className="text-sm font-semibold">{mail.subject}</div>
          <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 font-sans text-sm">
            {mail.body}
          </pre>
        </Modal>
      )}
    </div>
  );
}
